/*
 * Muori Styling modal
 *
 * The detail page is the real thing; this only borrows it. A card is an
 * ordinary link, and everything here is an enhancement layered on top of that,
 * so with JavaScript off, or if a fetch fails, the link still goes where it
 * says (FUNCTIONAL-REQUIREMENTS.md 8.2).
 *
 * A native <dialog> opened with showModal() is doing the hard parts: the focus
 * trap, Escape, and making the page behind it inert to keyboard and assistive
 * technology. Reimplementing those by hand would be worse in every way, so the
 * only piece added is scroll lock, which the element does not cover.
 *
 * History contract, in the terms 8.2 sets out:
 *   open        pushState once, so Back means "close" rather than "leave"
 *   arrows      replaceState, so a browse does not fill the history
 *   Back        closes and puts the list back where it was, focus included
 *   Forward     re-opens the same entry
 *   direct hit  never runs — that is the server-rendered page
 */
(function () {
  /*
   * Two screens open this: the Styling list, and the four entries on a product
   * page. Both mark their container, so neither is named here and a third
   * would need no change. The product page draws no previous/next arrows
   * (PDP-EDITORIAL-CONTENT-MODEL.md 6.2), so every use of them is guarded
   * rather than the file being forked.
   */
  var lists = document.querySelectorAll('[data-muori-styling-list]');
  var dialog = document.getElementById('MuoriStylingModal');
  if (!lists.length || !dialog || typeof dialog.showModal !== 'function') return;

  var panel = dialog.querySelector('.muori-modal__panel');
  var body = dialog.querySelector('.muori-modal__body');
  var prevBtn = dialog.querySelector('.muori-modal__arrow--prev');
  var nextBtn = dialog.querySelector('.muori-modal__arrow--next');
  var toTopBtn = dialog.querySelector('.muori-modal__to-top');
  var viewAll = dialog.querySelector('.muori-modal__view-all');
  var closeBtn = dialog.querySelector('.muori-modal__close');

  var trigger = null;
  var listScrollY = 0;
  var pushed = false;
  var tuckFrame = 0;

  /*
   * Keep navigation through the last picture, even when Outfit Details has
   * entered the viewport. Hide it only after the media column has scrolled
   * past the body's top edge, leaving the related-products portion in view.
   * The fixed header is outside that scrollport. Scrolling back to pictures
   * (including the header's up control) restores the arrows.
   */
  function updateArrowTuck() {
    /* A closed dialog is display:none, so every rect in it reads zero and the
       test below would answer about nothing. Opening calls this again. */
    if (!dialog.open || !prevBtn) return;

    var products = body.querySelector('.muori-detail__products');
    var media = body.querySelector('.muori-detail__media');
    if (!products || !media) {
      panel.classList.remove('muori-modal__panel--tucked');
      return;
    }

    panel.classList.toggle(
      'muori-modal__panel--tucked',
      media.getBoundingClientRect().bottom <= body.getBoundingClientRect().top
    );
  }

  function scheduleArrowTuck() {
    if (tuckFrame) return;
    tuckFrame = requestAnimationFrame(function () {
      tuckFrame = 0;
      updateArrowTuck();
    });
  }

  function fetchDetail(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var detail = doc.querySelector('.muori-detail');
        if (!detail) throw new Error('no detail content');
        return detail;
      });
  }

  /*
   * The fetched markup carries its own previous/next links. Reading the arrows
   * off them keeps one source of truth for the order — the modal never decides
   * for itself what comes next.
   */
  function applyNav(detail) {
    if (!prevBtn || !nextBtn) return;
    var prev = detail.querySelector('.muori-detail__nav-link--prev');
    var next = detail.querySelector('.muori-detail__nav-link--next');
    prevBtn.hidden = !prev;
    nextBtn.hidden = !next;
    prevBtn.dataset.url = prev ? prev.getAttribute('href') : '';
    nextBtn.dataset.url = next ? next.getAttribute('href') : '';
  }

  function load(url) {
    return fetchDetail(url).then(function (detail) {
      body.replaceChildren(detail);
      applyNav(detail);
      /* A new entry starts at its own beginning, not where the last one ended. */
      body.scrollTop = 0;
      updateArrowTuck();
    });
  }

  function show(url) {
    return load(url).then(function () {
      document.documentElement.classList.add('muori-modal-open');
      if (!dialog.open) dialog.showModal();
      body.focus();
      updateArrowTuck();
    });
  }

  function openFromList(url, el) {
    trigger = el || null;
    listScrollY = window.scrollY;
    show(url).then(
      function () {
        history.pushState({ muoriStyling: true }, '', url);
        pushed = true;
      },
      function () {
        /* Nothing was opened, so the link does what it always would. */
        window.location.href = url;
      }
    );
  }

  function teardown() {
    document.documentElement.classList.remove('muori-modal-open');
    if (dialog.open) dialog.close();
    window.scrollTo(0, listScrollY);
    if (trigger && document.contains(trigger)) trigger.focus();
    trigger = null;
    pushed = false;
  }

  /*
   * Closing goes through history rather than straight to teardown, so the URL
   * stops pointing at an entry that is no longer on screen. popstate then does
   * the actual closing, which means Back and the close button end up in exactly
   * the same state instead of two states that merely look alike.
   */
  function requestClose() {
    if (pushed) {
      history.back();
    } else {
      teardown();
    }
  }

  window.addEventListener('popstate', function (event) {
    if (event.state && event.state.muoriStyling) {
      /* Forward, back onto an entry. The trigger is gone; the entry is not. */
      if (!dialog.open) {
        pushed = true;
        show(window.location.pathname + window.location.search).catch(function () {
          window.location.reload();
        });
      }
      return;
    }
    if (dialog.open) teardown();
  });

  /* Escape reaches the dialog as `cancel`; routed through the same path as the
     button so it also corrects the URL. */
  dialog.addEventListener('cancel', function (event) {
    event.preventDefault();
    requestClose();
  });

  /*
   * View All is the only close control the design's header draws, so it is the
   * close button here rather than a second way to reach the same page. Closing
   * puts the list back where it was; following the link would reload it from
   * the top. Modified clicks are left alone — someone asking for a new tab
   * means the list itself, and gets it.
   */
  viewAll.addEventListener('click', function (event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button !== 0) return;
    event.preventDefault();
    requestClose();
  });

  /* A click that lands on the dialog itself is a click on the backdrop: the
     panel covers everything else. */
  dialog.addEventListener('click', function (event) {
    if (event.target === dialog) requestClose();
  });

  toTopBtn.addEventListener('click', function () {
    body.scrollTo({ top: 0, behavior: 'smooth' });
  });

  /*
   * SP の × 。View All と同じことをする — 幅で片方しか出ないので、二つある
   * ように見えることはない。
   */
  if (closeBtn) {
    closeBtn.addEventListener('click', requestClose);
  }

  /*
   * Nothing here handles the SP gallery. Its bar and thumbnails point at
   * pictures inside the entry that is already open, so they never move the
   * dialog off it — muori-styling-gallery.js owns them, including keeping the
   * fragment out of the address bar.
   */

  body.addEventListener('scroll', scheduleArrowTuck, { passive: true });
  /* Resizing can move both the media end and the scrollport's top edge. */
  window.addEventListener('resize', scheduleArrowTuck);

  [prevBtn, nextBtn].filter(Boolean).forEach(function (btn) {
    btn.addEventListener('click', function () {
      var url = btn.dataset.url;
      if (!url) return;
      load(url).then(
        function () {
          history.replaceState({ muoriStyling: true }, '', url);
        },
        function () {
          window.location.href = url;
        }
      );
    });
  });

  /* Any link inside a marked list is an entry; both screens put nothing else
     in there, so neither one's card markup has to be named. */
  Array.prototype.forEach.call(lists, function (list) {
    list.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      if (!link || !list.contains(link) || event.defaultPrevented) return;
      /* Anything the visitor meant to open elsewhere is left alone. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.button !== 0) return;
      event.preventDefault();
      openFromList(link.getAttribute('href'), link);
    });
  });
})();
